// Creación de empresa a partir de los datos del onboarding. Extraído de
// `Onboarding.handleSubmit` para compartirlo con el alta enterprise
// (`EnterpriseCheckout`) sin duplicar la secuencia (profiles → webhook
// `analizar_nombre_y_logo` → rubro → marca default). Ver ADR.
//
// NO hace refreshPermissions ni navega: eso queda en cada caller (depende del
// contexto/UI). Devuelve `{ ok, companyId }` o `{ ok:false, error }`.

import { supabase } from './supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import { ensureDefaultBrand } from './ensureDefaultBrand';
import { mergeLastCrearAnuncioState } from '../utils/crearAnuncioStorage';

const ANALIZAR_NOMBRE_Y_LOGO_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/analizar_nombre_y_logo';

export interface CreateCompanyInput {
  userId: string;
  name: string;
  pais: string;
  companyName: string;
  industry: string;
}

export interface CreateCompanyResult {
  ok: boolean;
  companyId?: string | null;
  error?: string;
}

export async function createCompanyForUser(
  input: CreateCompanyInput,
): Promise<CreateCompanyResult> {
  const name = input.name.trim();
  const industry = input.industry.trim();
  const companyName = input.companyName.trim();

  // 1) Guardar name + pais en profiles.
  const { error: profileError } = await supabase
    .from('profiles')
    .update({ name, pais: input.pais })
    .eq('id', input.userId);
  if (profileError) return { ok: false, error: profileError.message };

  // 2) Webhook que CREA la empresa (company_profiles) + setea id_company.
  let data: { response: 'success' | 'error'; description?: string };
  try {
    const response = await fetch(ANALIZAR_NOMBRE_Y_LOGO_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify({
        company_name: companyName,
        industry,
        user_id: input.userId,
        name,
        pais: input.pais,
      }),
    });
    data = await response.json();
  } catch {
    return { ok: false, error: 'No se pudo crear la empresa. Reintentá.' };
  }
  if (data.response === 'error') {
    return { ok: false, error: data.description || 'Error al crear la empresa.' };
  }

  // 3) Rubro + marca default (best-effort, no bloquea el alta).
  let companyId: string | null = null;
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', input.userId)
      .single();
    companyId = (profile as { id_company?: string | null } | null)?.id_company ?? null;
    if (companyId) {
      await supabase
        .from('company_profiles')
        .update({ rubro: industry })
        .eq('id_company', companyId);
      mergeLastCrearAnuncioState({ rubro: industry });
      await ensureDefaultBrand(companyId);
    }
  } catch {
    /* best-effort */
  }

  return { ok: true, companyId };
}
